import { useState, useMemo, useEffect } from 'react'
import { Navigate, Route, Routes, useParams } from 'react-router-dom'
import { Container } from '../../components/Container/Container'
import { Menu } from '../../components/Menu/Menu'
import { Communities } from './Communities/Communities'
import { Vendors } from './Vendors/Vendors'
import { Commons } from './Commons/Commons'
import {
  excludeVendorAndMyOrganizationTab,
  MY_COMMUNITY_KEYS,
  MY_COMMUNITY_TABS,
  MY_COMMUNITY_TABS_COMMONS,
  MY_COMMUNITY_TABS_GPO
} from './constants'
import './styles.scss'
import { Gpo } from './Gpo/Gpo'
import { useSelector } from 'react-redux'
import { getUser } from '../../redux/store/user/getters'
import { ROLES } from '../Permission'
import useRouter from '../../hooks/useRouter'
import { VENDORS_TABS_OPTIONS } from './Vendors/constants'
import { GpoDetails } from './GpoDetails/GpoDetails'
import { MyOrganizationCommunityContainer } from './MyOrganizationCommunity/MyOrganizationCommunityContainer'
import { MyOrganizationGpo } from './MyOrganizationGpo/MyOrganizationGpo'
import { MyOrganizationCommons } from './MyOrganizationCommons/MyOrganizationCommons'
import { MyOrganization } from './MyOrganization/MyOrganization'

export const MyCommunity = () => {
  const { query, push } = useRouter()
  const { tab } = useParams()
  const [activeTab, setActiveTab] = useState('')

  const user = useSelector(getUser)

  const tabsOptions = useMemo(() => {
    if (user.role.includes('vendor')) {
      return MY_COMMUNITY_TABS?.filter(
        (tab) => tab.key !== MY_COMMUNITY_KEYS.vendors
      )
    } else if (excludeVendorAndMyOrganizationTab.includes(user.role)) {
      return MY_COMMUNITY_TABS?.filter(
        (tab) => tab.key !== MY_COMMUNITY_KEYS.vendors
      )
    } else if (user.role === ROLES.HOSPITAL_ADMIN) {
      return MY_COMMUNITY_TABS.filter(
        (tab) => tab.key !== MY_COMMUNITY_KEYS.vendors
      )
    } else if (
      user.role === ROLES.COMMUNITY_PRESIDENT ||
      user.role === ROLES.VICE_PRESIDENT ||
      user.role === ROLES.CONTRACT_STEWARD ||
      user.role === ROLES.CLINICAL_COORDINATOR ||
      user.role === ROLES.COMMUNITY_RCA ||
      user.role === ROLES.LEGAL ||
      user.role === ROLES.ANALYST ||
      user.role === ROLES.HOSPITAL_USER ||
      user.role === ROLES.FINANCE
    ) {
      return MY_COMMUNITY_TABS
    } else if (
      user.role === ROLES.GPO_PRESIDENT ||
      user.role === ROLES.GPO_FINANCE_OFFICER
    ) {
      return MY_COMMUNITY_TABS_GPO
    } else if (user.role.includes('commons')) {
      return user.role === ROLES.COMMONS_VICE_PRESIDENT
        ? MY_COMMUNITY_TABS_COMMONS
        : MY_COMMUNITY_TABS_COMMONS?.filter(
            (tab) => tab.key !== MY_COMMUNITY_KEYS.gpo
          )
    } else {
      return MY_COMMUNITY_TABS?.filter(
        (tab) => tab.key !== MY_COMMUNITY_KEYS.my_organization
      )
    }
  }, [user])

  useEffect(() => {
    if (
      user.role === ROLES.GPO_PRESIDENT ||
      user.role === ROLES.GPO_FINANCE_OFFICER
    ) {
      setActiveTab(tab || MY_COMMUNITY_KEYS.my_organization)
    } else if (user.role.includes('commons')) {
      setActiveTab(tab || MY_COMMUNITY_KEYS.my_organization)
    } else if (!user.gpo) {
      setActiveTab(tab || MY_COMMUNITY_KEYS.communities)
    } else {
      setActiveTab(MY_COMMUNITY_KEYS.gpo_details)
    }
  }, [tab, user])

  useEffect(() => {
    if (
      query.tab === VENDORS_TABS_OPTIONS[0].value ||
      query.tab === VENDORS_TABS_OPTIONS[1].value
    ) {
      setActiveTab(MY_COMMUNITY_KEYS.vendors)
    }
  }, [query])

  const isTabsVisible = useMemo(() => {
    return (
      !user.gpo ||
      user.role === ROLES.GPO_PRESIDENT ||
      user.role === ROLES.GPO_FINANCE_OFFICER
    )
  }, [user])

  const defaultRoute = useMemo(() => {
    if (
      user?.role.includes('vendor') ||
      user.role === ROLES.COMMUNITY_PRESIDENT ||
      user.role === ROLES.VICE_PRESIDENT ||
      user.role === ROLES.CONTRACT_STEWARD ||
      user.role === ROLES.HOSPITAL_ADMIN ||
      user.role === ROLES.CLINICAL_COORDINATOR ||
      user.role === ROLES.GPO_PRESIDENT ||
      user.role === ROLES.GPO_FINANCE_OFFICER ||
      user.role === ROLES.COMMUNITY_RCA ||
      user.role === ROLES.LEGAL ||
      user.role === ROLES.ANALYST ||
      user.role === ROLES.HOSPITAL_USER ||
      user.role === ROLES.FINANCE ||
      user.role.includes('commons')
    ) {
      return MY_COMMUNITY_KEYS.my_organization
    } else if (user.role.includes('gpo')) {
      return MY_COMMUNITY_KEYS.gpo_details
    }
    return MY_COMMUNITY_KEYS.communities
  }, [user])

  return (
    <Container
      title="My Community"
      menu={
        isTabsVisible ? (
          <Menu
            mode="horizontal"
            onSelect={(e) => {
              setActiveTab(e.key)
              push(e.key)
            }}
            selectedKeys={[activeTab]}
            defaultSelectedKeys={[activeTab]}
            items={tabsOptions}
          />
        ) : undefined
      }
    >
      <Routes>
        <Route path={MY_COMMUNITY_KEYS.communities} element={<Communities />} />
        <Route path={MY_COMMUNITY_KEYS.gpo} element={<Gpo />} />
        <Route path={MY_COMMUNITY_KEYS.vendors} element={<Vendors />}>
          <Route path=":tab" element={<Vendors />} />
        </Route>
        <Route path={MY_COMMUNITY_KEYS.commons} element={<Commons />} />
        {user.vendor ? (
          <Route
            path={MY_COMMUNITY_KEYS.my_organization}
            element={<MyOrganization />}
          >
            <Route path=":tab" element={<MyOrganization />} />
          </Route>
        ) : user?.gpo ? (
          <>
            <Route
              path={MY_COMMUNITY_KEYS.my_organization}
              element={<MyOrganizationGpo />}
            >
              <Route path=":tab" element={<MyOrganizationGpo />} />
            </Route>
          </>
        ) : user?.commons ? (
          <Route
            path={MY_COMMUNITY_KEYS.my_organization}
            element={<MyOrganizationCommons />}
          >
            <Route path=":tab" element={<MyOrganizationCommons />} />
          </Route>
        ) : (
          <Route
            path={MY_COMMUNITY_KEYS.my_organization}
            element={<MyOrganizationCommunityContainer />}
          >
            <Route path=":tab" element={<MyOrganizationCommunityContainer />} />
          </Route>
        )}
        <Route path={MY_COMMUNITY_KEYS.gpo_details} element={<GpoDetails />} />
        <Route index element={<Navigate to={defaultRoute} replace />} />
      </Routes>
    </Container>
  )
}
